import React, { useEffect, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import type { Snippet } from '../types/snippet.ts';

import { SnippetPanelCreateItem } from './snippet-panel-item/SnippetPanelCreateItem.tsx';
import { useSnippetPopupPreview } from './preview/SnippetPopupPreviewContext.tsx';
import { SnippetPanelItem } from './snippet-panel-item/SnippetPanelItem.tsx';
import { SNIPPET_CARD_HEIGHT } from './snippet-panel-item/SnippetPanelCard.tsx';

const styles = cssMap({
	container: {
		flexGrow: 1,
		minHeight: '0px',
		marginInline: token('space.negative.200'),
		overflow: 'hidden',
	},
});

export const useVirtualizedListHeight = (
	containerRef: React.RefObject<HTMLDivElement | null>,
): { containerHeight: number } => {
	const [containerHeight, setContainerHeight] = useState(0);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				setContainerHeight(entry.contentRect.height);
			}
		});

		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef]);

	return { containerHeight };
};

type SnippetPanelListProps = {
	snippets: Snippet[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelList = ({
	snippets,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelListProps): React.JSX.Element => {
	const { hidePreview } = useSnippetPopupPreview();

	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<List>(null);

	const { containerHeight } = useVirtualizedListHeight(containerRef);

	useEffect(() => {
		// Scroll to the top of the list when the snippets change as sort order may not be "most recent"
		listRef.current?.scrollTo(0);
	}, [snippets]);

	const isCreateRow = (index: number) =>
		!fg('platform_synced_block_patch_14') && index === snippets.length;
	const itemCount = snippets.length + (fg('platform_synced_block_patch_14') ? 0 : 1);

	const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => (
		<div style={style} role="listitem">
			{isCreateRow(index) ? (
				<SnippetPanelCreateItem contentId={contentId} spaceId={spaceId} spaceKey={spaceKey} />
			) : (
				<SnippetPanelItem
					snippet={snippets[index]}
					contentId={contentId}
					spaceId={spaceId}
					spaceKey={spaceKey}
				/>
			)}
		</div>
	);

	return (
		<Box xcss={styles.container} ref={containerRef} onMouseLeave={hidePreview}>
			{containerHeight > 0 && (
				<List
					ref={listRef}
					height={containerHeight}
					itemCount={itemCount}
					itemSize={SNIPPET_CARD_HEIGHT}
					width="100%"
					itemKey={(index) =>
						isCreateRow(index) ? 'snippet-panel-create-row' : snippets[index].snippetId
					}
					overscanCount={3}
				>
					{Row}
				</List>
			)}
		</Box>
	);
};
